import React, { useEffect, useState } from 'react';
import type { VisitsLeadOriginFilter, VisitsLeadStatusFilter } from '@troia-v3/types';

import { FilterBottomSheet, FiltersButton } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

export interface LeadFilters {
  origin?: VisitsLeadOriginFilter;
  status?: VisitsLeadStatusFilter;
}

interface Props {
  value: LeadFilters;
  onChange: (value: LeadFilters) => void;
}

const ORIGIN_OPTIONS: Array<[VisitsLeadOriginFilter | undefined, string]> = [
  [undefined, 'Todos os leads'],
  ['capture_page', 'Só da página de captura'],
];

/** Mesmos rótulos do status no CRM (Pendente / Ganho / Perdido). */
const STATUS_OPTIONS: Array<[VisitsLeadStatusFilter | undefined, string]> = [
  [undefined, 'Todos os status'],
  ['pending', 'Pendentes'],
  ['won', 'Ganhos'],
  ['lost', 'Perdidos'],
];

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider"
    style={{ color: '#94a3b8' }}
  >
    {children}
  </p>
);

const OptionButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}> = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
    style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
    onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
    data-testid={testId}
  >
    {label}
    {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
  </button>
);

/**
 * Filtros dos LEADS da tela de visitas — procedência e status.
 *
 * Mexem só no que conta leads (barra, card e mapa de calor em "Leads"); visitas
 * e vendas não mudam. Por isso o título do popover diz "Leads", e não
 * "Filtros": quem liga "Ganhos" não pode achar que as visitas encolheram.
 *
 * Escolher uma opção NÃO fecha o popover — são duas perguntas independentes, e
 * fechar a cada clique obrigaria a reabrir para responder a segunda.
 */
export const LeadFiltersButton: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const activeCount = (value.origin ? 1 : 0) + (value.status ? 1 : 0);

  // Superfície transiente: fecha no Esc (o clique fora é o scrim abaixo).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const set = (next: LeadFilters): void => {
    onChange({
      ...(next.origin ? { origin: next.origin } : {}),
      ...(next.status ? { status: next.status } : {}),
    });
  };

  const content = (
    <>
      <SectionTitle>Procedência do lead</SectionTitle>
      {ORIGIN_OPTIONS.map(([origin, label]) => (
        <OptionButton
          key={label}
          label={label}
          active={value.origin === origin}
          onClick={() => set({ ...value, origin })}
          testId={`analytics-lead-filter-origin-${origin ?? 'all'}`}
        />
      ))}

      <div className="my-1.5 border-t" style={{ borderColor: '#e5e7eb' }} />

      <SectionTitle>Status do lead</SectionTitle>
      {STATUS_OPTIONS.map(([status, label]) => (
        <OptionButton
          key={label}
          label={label}
          active={value.status === status}
          onClick={() => set({ ...value, status })}
          testId={`analytics-lead-filter-status-${status ?? 'all'}`}
        />
      ))}

      <p className="px-2.5 pt-2 pb-1 text-xs" style={{ color: '#94a3b8' }}>
        Valem para os leads. Visitas e vendas não mudam.
      </p>
    </>
  );

  return (
    <div className="relative flex-none">
      <FiltersButton
        count={activeCount}
        active={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="analytics-lead-filters"
      />

      {open && (
        <div className="hidden sm:block">
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-2 z-[100] w-64 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
            style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
            role="dialog"
            aria-label="Filtros de leads"
            data-testid="analytics-lead-filters-popover"
          >
            {content}
          </div>
        </div>
      )}

      <FilterBottomSheet
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Filtros de leads"
        data-testid="analytics-lead-filters-sheet"
      >
        {content}
      </FilterBottomSheet>
    </div>
  );
};

LeadFiltersButton.displayName = 'LeadFiltersButton';
